import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { FormProps } from '@formio/react/lib/components/Form';
import Button from '@components/ui/Button/Button';
import FullPageStepper from '@components/shared/FullPageStepper/FullPageStepper';
import LogoutButton from '@components/shared/LogoutButton/LogoutButton';

import type { FullPageStepperStep } from '@components/shared/FullPageStepper/FullPageStepper';
import { getFormConfigById } from '@/services/formService';
import { saveFormData, getFormData } from '@/services/formDataService';
import { WorkpayApiError } from '@lib/apiEnvelope';
import {
  getTenantById,
  requestTenantActivation,
  requestTenantActivationSkipReview,
} from '@/services/tenantService';
import { getCommercialTerms } from '@/services/commercialTermService';
import { patchFileComponents, setCurrentWorkflow } from '@/lib/formioFileProvider';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@stores/notificationStore';
import { useTranslation } from '@/hooks/useTranslation';
import { useOnboardingForms } from './useOnboardingForms';
import { useOnboardingDismissed } from './useOnboardingDismissed';
import RequestActivationModal from './RequestActivationModal';
import type { ActivationMode } from './RequestActivationModal';
import useFormioResize from '@hooks/useFormioResize';
import useMultiStepForm from '@hooks/useMultiStepForm';
import styles from './RequestedDetailsPage.module.scss';

const Form = lazy(() =>
  import('@formio/react').then((mod) => ({ default: mod.Form })),
);

// ============================================
// TYPES
// ============================================

interface FormioInstance {
  checkValidity: (data: Record<string, unknown>, dirty: boolean) => boolean;
}

// ============================================
// STEP FORM RENDERER
// ============================================

interface StepFormProps {
  formConfigId: string;
  objectType: string;
  objectId: string;
  onSaved: () => void;
  onError: () => void;
  /** Ref to expose the save handler to parent for footer button */
  saveRef: React.MutableRefObject<(() => boolean) | null>;
}

function StepForm({ formConfigId, objectType, objectId, onSaved, onError, saveRef }: StepFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const formRef = useRef<Record<string, unknown> | null>(null);
  const instanceRef = useRef<FormioInstance | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: formConfig, isLoading: configLoading, isError: configError } = useQuery({
    queryKey: ['formConfig', formConfigId],
    queryFn: () => getFormConfigById(formConfigId),
  });

  const { data: savedFormData, isLoading: dataLoading } = useQuery({
    queryKey: ['formData', objectType, objectId, formConfigId],
    queryFn: () => getFormData(objectType, objectId, formConfigId),
    enabled: !!objectType && !!objectId,
  });

  const submission = useMemo(() => {
    if (savedFormData?.data) {
      return { data: savedFormData.data } as Record<string, unknown>;
    }
    return undefined;
  }, [savedFormData]);

  const submitMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      saveFormData(objectType, objectId, formConfigId, { data, status: 'SUBMITTED' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['formData', objectType, objectId, formConfigId] });
      onSaved();
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
      onError();
    },
  });

  // Set workflow context for file uploads
  useEffect(() => {
    setCurrentWorkflow(objectType === 'TENANT' ? 'TENANT_FORM' : '');
    return () => setCurrentWorkflow('');
  }, [objectType]);

  // Patch file components to route through file-management-service
  const patchedFormConfig = useMemo(() => {
    if (!formConfig?.formConfig) return null;
    return patchFileComponents(
      JSON.parse(JSON.stringify(formConfig.formConfig)) as Record<string, unknown>,
    );
  }, [formConfig?.formConfig]);

  useFormioResize(formConfig?.formConfig);

  const handleChange = useCallback((submission: { data: Record<string, unknown> }) => {
    formRef.current = submission.data;
  }, []);

  const handleFormReady = useCallback((instance: unknown) => {
    instanceRef.current = instance as FormioInstance;
  }, []);

  const handleSave = useCallback((): boolean => {
    const formData = formRef.current ?? {};
    const instance = instanceRef.current;
    if (instance) {
      const isValid = instance.checkValidity(formData, true);
      if (!isValid) {
        const errorEl = containerRef.current?.querySelector('.has-error, .formio-error-wrapper');
        if (errorEl) {
          errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return false;
      }
    }
    submitMutation.mutate(formData);
    return true;
  }, [submitMutation]);

  // Expose save handler to parent
  saveRef.current = handleSave;

  const isLoading = configLoading || dataLoading;

  if (isLoading) {
    return <div className={styles.loading}>{t('K_LOADING_FORM', 'Loading form...')}</div>;
  }

  if (configError || !patchedFormConfig) {
    return <div className={styles.error}>{t('K_FAILED_LOAD_FORM', 'Failed to load form.')}</div>;
  }

  return (
    <div ref={containerRef}>
      <div className={styles.formRoot}>
        <Suspense fallback={<div className={styles.loading}>{t('K_LOADING_FORM', 'Loading form...')}</div>}>
          <Form
            src={patchedFormConfig as unknown as FormProps['src']}
            onChange={handleChange}
            formReady={handleFormReady}
            submission={submission as FormProps['submission']}
          />
        </Suspense>
      </div>
    </div>
  );
}

// ============================================
// PAGE COMPONENT
// ============================================

function RequestedDetailsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dismiss = useOnboardingDismissed((s) => s.dismiss);
  const addNotification = useNotificationStore((s) => s.addNotification);
  const { forms, isLoading, isError, refetch } = useOnboardingForms();
  const session = useAuthStore((s) => s.session);
  const objectType = session?.organization.type ?? '';
  const objectId = session?.tenant.id ?? '';

  // Fetch tenant details for read-only Step 1 + status check
  const { data: tenantData, isLoading: tenantLoading } = useQuery({
    queryKey: ['tenant', objectId],
    queryFn: () => getTenantById(objectId),
    enabled: !!objectId,
  });

  // Fetch commercial terms for read-only Step 2
  const { data: commercialTermsData, isLoading: commercialTermsLoading } = useQuery({
    queryKey: ['commercialTerms', 'TENANT', objectId],
    queryFn: () => getCommercialTerms('TENANT', objectId, { size: 100 }),
    enabled: !!objectId,
  });

  const STATIC_STEPS_COUNT = 2;

  const saveRef = useRef<(() => boolean) | null>(null);
  const [savingStep, setSavingStep] = useState(false);
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [activationSubmitting, setActivationSubmitting] = useState(false);

  const handleActivationConfirm = useCallback(
    async (mode: ActivationMode) => {
      setActivationSubmitting(true);
      try {
        // BE returns the updated tenant; route directly from its status so we
        // don't depend on /t/index.tsx doing a live fetch (session.tenant.status
        // is frozen at login and won't reflect this just-applied transition).
        const result =
          mode === 'skip_review'
            ? await requestTenantActivationSkipReview(objectId)
            : await requestTenantActivation(objectId);
        setShowActivationModal(false);
        void queryClient.invalidateQueries({ queryKey: ['tenant', objectId] });
        dismiss();
        // Exhaustive over TenantStatus — silently falling through on an
        // unexpected value would land the user somewhere wrong (e.g. still
        // ONBOARDING → /t/employees would loop back here via t.tsx).
        switch (result.tenant.status) {
          case 'CONTRACT_REVIEW_PENDING':
            void navigate({ to: '/t/contracts' });
            break;
          case 'ACTIVATION_PENDING':
          case 'ACTIVE':
            void navigate({ to: '/t/employees' });
            break;
          case 'ONBOARDING':
          case 'INACTIVE':
          default:
            addNotification({
              variant: 'critical',
              title: t(
                'K_NOTIF_UNEXPECTED_ACTIVATION_STATE',
                'Activation succeeded but the response status was unexpected. Please refresh.',
              ),
            });
            break;
        }
      } catch (error) {
        const e = error as WorkpayApiError;
        addNotification({
          variant: 'critical',
          title: t(e.description, e.description),
        });
      } finally {
        setActivationSubmitting(false);
      }
    },
    [objectId, queryClient, dismiss, navigate, addNotification, t],
  );

  // Build steps: 2 static read-only steps + form steps
  const steps: FullPageStepperStep[] = useMemo(
    () => {
      const staticSteps: FullPageStepperStep[] = [
        {
          id: 'tenant-details',
          label: t('K_STEP_TENANT_DETAILS', 'Client Details'),
          title: t('K_STEP_TENANT_DETAILS', 'Client Details'),
          description: t('K_STEP_TENANT_DETAILS_DESC', 'Review your company details.'),
        },
        {
          id: 'commercial-terms',
          label: t('K_STEP_COMMERCIAL_TERMS', 'Commercial Terms'),
          title: t('K_STEP_COMMERCIAL_TERMS', 'Commercial Terms'),
          description: t('K_STEP_COMMERCIAL_TERMS_DESC', 'Review your commercial terms.'),
        },
      ];
      const formSteps = forms.map((f) => ({
        id: f.formConfigId,
        label: f.formName,
        title: f.formName,
        description: t('K_STEP_FORM_DESC', 'Complete the {name} form to continue.').replace('{name}', f.formName.toLowerCase()),
        saved: f.status === 'saved',
      }));
      return [...staticSteps, ...formSteps];
    },
    [forms, t],
  );

  const stepper = useMultiStepForm({
    totalSteps: steps.length,
  });

  const formStepIndex = stepper.currentStep - STATIC_STEPS_COUNT;
  const currentForm = formStepIndex >= 0 ? forms[formStepIndex] : undefined;

  const handleStepSaved = useCallback(async () => {
    setSavingStep(false);
    refetch();
    if (stepper.isLastStep) {
      setShowActivationModal(true);
    } else {
      stepper.goToNext();
    }
  }, [stepper, refetch]);

  const handleStepError = useCallback(() => {
    setSavingStep(false);
  }, []);

  const handleContinue = useCallback(() => {
    // Last static step with no forms — open activation choice modal
    if (stepper.isLastStep && stepper.currentStep < STATIC_STEPS_COUNT) {
      setShowActivationModal(true);
      return;
    }
    // Static read-only steps — just advance
    if (stepper.currentStep < STATIC_STEPS_COUNT) {
      void stepper.goToNext();
      return;
    }
    // Form steps — save with validation
    if (saveRef.current) {
      const submitted = saveRef.current();
      if (submitted) {
        setSavingStep(true);
      }
    }
  }, [stepper]);

  if (isLoading || tenantLoading || commercialTermsLoading) {
    return (
      <div className={styles.loadingPage}>
        <p>{t('K_LOADING_REQUIRED_DETAILS', 'Loading required details...')}</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.errorPage}>
        <p>{t('K_FAILED_LOAD_REQUIRED_DETAILS', 'Failed to load required details.')}</p>
        <Button variant="outline" size="sm" onClick={refetch}>
          {t('K_BUTTON_RETRY', 'Retry')}
        </Button>
      </div>
    );
  }


  return (
    <FullPageStepper
      pageTitle={t('K_PAGE_TITLE_REQUIRED_DETAILS', 'Required Details')}
      steps={steps}
      activeStep={stepper.currentStep}
      onClose={() => {}}
      onStepClick={(i) => stepper.goToStep(i)}
      hideCloseButton
      headerAction={<LogoutButton />}
      footer={
        <div className={styles.footer}>
          <div />
          <div className={styles.footerRight}>
            {!stepper.isFirstStep && (
              <Button variant="outline" size="md" onClick={() => stepper.goToPrev()}>
                {t('K_BACK', 'Back')}
              </Button>
            )}
            <Button
              variant="filled"
              size="md"
              onClick={handleContinue}
              loading={savingStep}
            >
              {stepper.isLastStep
                ? t('K_BUTTON_SUBMIT', 'Submit')
                : t('K_BUTTON_CONTINUE', 'Continue')}
            </Button>
          </div>
        </div>
      }
    >
      {/* Step 1: Tenant Details (read-only) */}
      {stepper.currentStep === 0 && tenantData && (
        <div className={styles.detailsGrid}>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>{t('K_LABEL_ORGANISATION_NAME', 'Company Name')}</span>
            <span className={styles.detailValue}>{tenantData.tenant.name}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>{t('K_LABEL_COUNTRY', 'Country')}</span>
            <span className={styles.detailValue}>{tenantData.tenant.country.name}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>{t('K_LABEL_PRODUCT_TYPES', 'Product Types')}</span>
            <span className={styles.detailValue}>
              {tenantData.tenant.productTypes.map((pt) => pt.productType).join(', ')}
            </span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>{t('K_LABEL_STATUS', 'Status')}</span>
            <span className={styles.detailValue}>{tenantData.tenant.status}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>{t('K_LABEL_FIRST_NAME', 'First Name')}</span>
            <span className={styles.detailValue}>{tenantData.user.firstName}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>{t('K_LABEL_LAST_NAME', 'Last Name')}</span>
            <span className={styles.detailValue}>{tenantData.user.lastName}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>{t('K_LABEL_EMAIL', 'Email')}</span>
            <span className={styles.detailValue}>{tenantData.user.email}</span>
          </div>
          {tenantData.user.mobile && (
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>{t('K_LABEL_MOBILE', 'Mobile')}</span>
              <span className={styles.detailValue}>{tenantData.user.mobile}</span>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Commercial Terms (read-only flat table) */}
      {stepper.currentStep === 1 && (
        <>
          {(commercialTermsData?.content ?? []).length === 0 ? (
            <p className={styles.placeholderText}>
              {t('K_NO_COMMERCIAL_TERMS', 'No commercial terms configured.')}
            </p>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.readOnlyTable}>
                <thead>
                  <tr>
                    <th>{t('K_LABEL_PRODUCT_TYPE', 'Product Type')}</th>
                    <th>{t('K_LABEL_COUNTRY', 'Country')}</th>
                    <th>{t('K_LABEL_EMPLOYMENT_TYPE', 'Employment Type')}</th>
                    <th>{t('K_LABEL_EMPLOYEE_COUNT', 'Employee Count')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(commercialTermsData?.content ?? []).map((term) => (
                    <tr key={term.id}>
                      <td>{term.productType.productType}</td>
                      <td>{term.country.name}</td>
                      <td>{term.employmentType?.name ?? '-'}</td>
                      <td>{term.employeeCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Form steps */}
      {currentForm && (
        <StepForm
          key={currentForm.formConfigId}
          formConfigId={currentForm.formConfigId}
          objectType={objectType}
          objectId={objectId}
          onSaved={handleStepSaved}
          onError={handleStepError}
          saveRef={saveRef}
        />
      )}

      <RequestActivationModal
        isOpen={showActivationModal}
        onClose={() => setShowActivationModal(false)}
        onConfirm={handleActivationConfirm}
        isSubmitting={activationSubmitting}
      />
    </FullPageStepper>
  );
}

export default RequestedDetailsPage;
